import React, { useCallback } from 'react';
import { useSettingsStore } from '@/stores';
import { useSettingsQueries } from '../settings/use-settings-queries';

interface GlobalSettings {
  theme: 'light' | 'dark';
  language: string;
  timezone: string;
  currency: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export const useGlobalSettings = () => {
  const { 
    globalSettings, 
    setGlobalSettings, 
    updateGlobalSettings 
  } = useSettingsStore();

  // TanStack Query hooks - return data directly for GoogleAuthProvider compatibility
  const { data: serverSettings, isLoading, error } = useSettingsQueries();

  const fetchGlobalSettings = useCallback(async (): Promise<void> => {
    // TanStack Query handles fetching automatically
    if (serverSettings) {
      // Convert server settings to our GlobalSettings format
      const globalSettings: GlobalSettings = {
        theme: 'light',
        language: 'en',
        timezone: 'UTC',
        currency: 'USD',
        ...serverSettings
      };
      setGlobalSettings(globalSettings);
    }
  }, [serverSettings, setGlobalSettings]);

  // Auto-fetch settings when serverSettings change
  React.useEffect(() => {
    fetchGlobalSettings();
  }, [fetchGlobalSettings]);

  const saveGlobalSettings = useCallback(async (settings: Partial<GlobalSettings>): Promise<void> => {
    // Since there's no PUT endpoint, just update local state
    updateGlobalSettings(settings);
  }, [updateGlobalSettings]);

  const updateSetting = useCallback((key: string, value: unknown): void => {
    updateGlobalSettings({ [key]: value });
  }, [updateGlobalSettings]);

  return {
    data: serverSettings, // Return data for GoogleAuthProvider compatibility
    globalSettings: globalSettings,
    isLoading,
    error,
    fetchGlobalSettings,
    saveGlobalSettings,
    updateSetting,
  };
};
